import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isNetworkError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isNetworkError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Check if the error is a network error
    const isNetworkError =
      error.message.includes("Failed to fetch") ||
      error.message.includes("Network request failed") ||
      error.message.includes("ERR_NAME_NOT_RESOLVED") ||
      (error.name === "TypeError" &&
        "message" in error &&
        (error.message.includes("fetch") || error.message.includes("network")));

    return {
      hasError: true,
      error,
      isNetworkError,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleRetry = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.state.isNetworkError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-6xl mb-4">🔌</div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Connection Error
              </h1>
              <p className="text-gray-600 mb-6">
                We're having trouble connecting to our servers. This might be
                due to network issues or server maintenance.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Error: {this.state.error?.message || "Network request failed"}
              </p>
              <button
                onClick={this.handleRetry}
                className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors"
              >
                Retry Connection
              </button>
              <div className="mt-6 text-sm text-gray-500">
                <p>
                  If the problem persists, please try again later or contact
                  support.
                </p>
              </div>
            </div>
          </div>
        );
      }

      // Fallback UI for other types of errors
      return (
        this.props.fallback || (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Something went wrong
              </h1>
              <p className="text-gray-600 mb-6">
                We're sorry, but we encountered an unexpected error.
              </p>
              <button
                onClick={this.handleRetry}
                className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
